import { IntegrationType } from '@doss/prisma/types'
import { z } from 'zod'
import { defineSystemEvent } from './definition.js'
import { systemEventFailureDataSchema } from './primitives.js'

const integrationData = z.strictObject({
  integration_id: z.uuid(),
  integration_type: z.enum(IntegrationType),
})
const integrationAttempt = integrationData.partial({ integration_id: true })
const providerOperationData = integrationData.extend({
  operation: z.string().min(1),
  // External IDs are strings; they are not Doss entity UUIDs.
  provider_request_id: z.string().min(1).optional(),
  records_processed: z.number().int().nonnegative().optional(),
})
const webhookReceiptData = z.strictObject({
  integration_id: z.uuid().optional(),
  provider: z.string().min(1),
  // Subscriptions have provider IDs, not Doss entity UUIDs. Some providers return no ID.
  provider_webhook_id: z.string().min(1).optional(),
  receipt_id: z.string().min(1),
  provider_event_id: z.string().min(1).optional(),
  topic: z.string().optional(),
})
const emailData = z.strictObject({
  send_id: z.string().min(1),
  provider: z.string().min(1),
  provider_message_id: z.string().min(1).optional(),
})

export const integrationSystemEvents = {
  integrationCreateSuccess: defineSystemEvent({
    type: 'CREATE_INTEGRATION',
    status: 'SUCCESS',
    entityTypes: ['INTEGRATION'],
    data: integrationData,
  }),
  integrationCreateFailed: defineSystemEvent({
    type: 'CREATE_INTEGRATION',
    status: 'FAIL',
    entityTypes: ['INTEGRATION'],
    data: integrationAttempt.extend(systemEventFailureDataSchema.shape),
  }),
  integrationDeleteSuccess: defineSystemEvent({
    type: 'DELETE_INTEGRATION',
    status: 'SUCCESS',
    entityTypes: ['INTEGRATION'],
    data: integrationData,
  }),
  integrationDeleteFailed: defineSystemEvent({
    type: 'DELETE_INTEGRATION',
    status: 'FAIL',
    entityTypes: ['INTEGRATION'],
    data: integrationData.extend(systemEventFailureDataSchema.shape),
  }),
  integrationAuthSuccess: defineSystemEvent({
    type: 'AUTH_INTEGRATION',
    status: 'SUCCESS',
    entityTypes: ['INTEGRATION'],
    data: integrationData,
  }),
  integrationAuthFailed: defineSystemEvent({
    type: 'AUTH_INTEGRATION',
    status: 'FAIL',
    entityTypes: ['INTEGRATION'],
    data: integrationAttempt.extend(systemEventFailureDataSchema.shape),
  }),
  integrationReadSuccess: defineSystemEvent({
    type: 'READ_INTEGRATION',
    status: 'SUCCESS',
    entityTypes: ['INTEGRATION'],
    data: providerOperationData,
  }),
  integrationReadFailed: defineSystemEvent({
    type: 'READ_INTEGRATION',
    status: 'FAIL',
    entityTypes: ['INTEGRATION'],
    data: providerOperationData.extend(systemEventFailureDataSchema.shape),
  }),
  integrationWriteSuccess: defineSystemEvent({
    type: 'WRITE_INTEGRATION',
    status: 'SUCCESS',
    entityTypes: ['INTEGRATION'],
    data: providerOperationData,
  }),
  integrationWriteFailed: defineSystemEvent({
    type: 'WRITE_INTEGRATION',
    status: 'FAIL',
    entityTypes: ['INTEGRATION'],
    data: providerOperationData.extend(systemEventFailureDataSchema.shape),
  }),
  webhookReceiveSuccess: defineSystemEvent({
    type: 'RECEIVE_WEBHOOK',
    status: 'SUCCESS',
    entityTypes: ['WEBHOOK'],
    data: webhookReceiptData,
  }),
  webhookReceiveFailed: defineSystemEvent({
    type: 'RECEIVE_WEBHOOK',
    status: 'FAIL',
    entityTypes: ['WEBHOOK'],
    data: webhookReceiptData.extend(systemEventFailureDataSchema.shape),
  }),
  // Success means provider acceptance. Target the associated user, membership, step, or v2 workflow
  // when known.
  emailSendSuccess: defineSystemEvent({
    type: 'SEND_EMAIL',
    status: 'SUCCESS',
    entityTypes: ['USER', 'MEMBERSHIP', 'ACTION_STEP', 'WORKFLOW_V2', null],
    data: emailData,
  }),
  emailSendFailed: defineSystemEvent({
    type: 'SEND_EMAIL',
    status: 'FAIL',
    entityTypes: ['USER', 'MEMBERSHIP', 'ACTION_STEP', 'WORKFLOW_V2', null],
    data: emailData.extend(systemEventFailureDataSchema.shape),
  }),
}
